/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import {
  genericSelectors,
  layerActions,
  syncGroupsSelector,
  useRegisterFeatureSyncSource,
  useUpdateFeatureSyncLayers,
} from '@opengeoweb/store';
import { webmapUtils } from '@opengeoweb/webmap';
import {
  createWarningFeatureCollection,
  getSelectedFormAreaIds,
  shouldExcludeWarningFromFeatureSync,
} from './warningListFeatureSyncUtils';
import type { SyncedWarningFeature } from './warningListFeatureSyncUtils';
import type { WarningModuleStore } from '../../store/types';
import { warningsFiltersActions } from '../../store/warningsFilters/reducer';
import { getHoveredWarningIdsByPanelId } from '../../store/warningsFilters/selectors';
import type { Warning } from '../../store/warningsForm/types';
import { isExpireMessage } from '../../utils/warningUtils';
import { warningLevelFeatureColors } from '../PublicWarningsForm';
import type { WarningLevel } from '../PublicWarningsForm/LevelField';

export const WARNING_HOVER_DISPATCH_DELAY = 50;

interface UseWarningListFeatureSyncProps {
  filteredWarningsByExpandedSections: Warning[];
  hiddenWarningIds: Set<string>;
  panelId: string;
  selectedFormWarning?: Warning;
  selectedWarningId?: string;
}

interface UseWarningListFeatureSyncResult {
  handleHoverWarning: (warningId?: string) => void;
  hoveredFeatureId?: string;
}

export const useWarningListFeatureSync = ({
  filteredWarningsByExpandedSections,
  hiddenWarningIds,
  panelId,
  selectedFormWarning,
  selectedWarningId,
}: UseWarningListFeatureSyncProps): UseWarningListFeatureSyncResult => {
  const dispatch = useDispatch();
  const store = useStore<WarningModuleStore>();
  const selectedFormAreaIds = React.useMemo(
    () => getSelectedFormAreaIds(selectedFormWarning),
    [selectedFormWarning],
  );

  const allFeatures = React.useMemo(
    (): SyncedWarningFeature[] =>
      filteredWarningsByExpandedSections
        .flatMap((warning) => {
          const id = warning?.id;
          if (!id) {
            return [];
          }

          const warningIds = [warning?.id, warning?.warningDetail?.id].filter(
            (warningId): warningId is string => Boolean(warningId),
          );
          const hasMatchingAreaId = (warning?.warningDetail?.areas ?? []).some(
            (area) =>
              area.areaId !== undefined && selectedFormAreaIds.has(area.areaId),
          );

          if (
            shouldExcludeWarningFromFeatureSync({
              hiddenWarningIds,
              hasMatchingAreaId,
              selectedFormWarning,
              selectedWarningId,
              warningIds,
            })
          ) {
            return [];
          }

          const originalId = id;
          const areas = warning?.warningDetail?.areas ?? [];

          return areas
            .filter((area) => !!area.geoJSON)
            .map((area) => {
              const level = warning?.warningDetail?.level;
              const isAviationWarning = level === 'SIG' || level === 'AIR';
              const styledGeoJSON =
                level || isExpireMessage(warning)
                  ? warningLevelFeatureColors(
                      isAviationWarning
                        ? undefined
                        : (level as WarningLevel | undefined),
                      area.geoJSON!,
                      isAviationWarning,
                      isExpireMessage(warning),
                    )
                  : area.geoJSON!;

              return {
                id: originalId + (area.areaId ?? ''),
                originalId,
                geoJSON: styledGeoJSON,
              };
            });
        })
        .filter((feature) => !!feature.id),
    [
      filteredWarningsByExpandedSections,
      hiddenWarningIds,
      selectedFormAreaIds,
      selectedFormWarning,
      selectedWarningId,
    ],
  );

  useRegisterFeatureSyncSource(panelId);

  const warningFeatureCollection = React.useMemo(
    () => createWarningFeatureCollection(allFeatures),
    [allFeatures],
  );

  const warningLayerId = React.useRef(webmapUtils.generateLayerId()).current;

  const warningFeatureLayersById = React.useMemo(
    () => ({
      [warningLayerId]: {
        id: warningLayerId,
        geojson: warningFeatureCollection,
      },
    }),
    [warningFeatureCollection, warningLayerId],
  );

  useUpdateFeatureSyncLayers(warningFeatureLayersById, panelId);

  const workspaceViewsById = useSelector((state: WarningModuleStore) =>
    syncGroupsSelector.getWorkspaceViewsById(state),
  ) as Record<string, { componentType?: string }> | undefined;
  const ownedFeatureLayerIds = useSelector(
    (state: WarningModuleStore) =>
      genericSelectors.selectFeatureSyncLayerIdsForPanel(state, panelId),
    shallowEqual,
  );
  const linkedTargetIds = useSelector(
    (state: WarningModuleStore) =>
      genericSelectors.getLinkedTargetIds(state, panelId),
    shallowEqual,
  );
  const hoveredLinkedFeatureIds = useSelector(
    (state: WarningModuleStore) =>
      genericSelectors.selectHoveredFeatureIdsForPanel(state, panelId),
    shallowEqual,
  );
  const hoveredLocalWarningIds = useSelector((state: WarningModuleStore) =>
    getHoveredWarningIdsByPanelId(state, panelId),
  );
  const hasLinkedTimelineTarget = linkedTargetIds.some(
    (targetId) =>
      workspaceViewsById?.[targetId]?.componentType === 'WarningsTimeline',
  );
  const [localHoveredWarningId, setLocalHoveredWarningId] = React.useState<
    string | undefined
  >(undefined);
  const [isUsingLocalHoverOverride, setIsUsingLocalHoverOverride] =
    React.useState(false);
  const hoverDispatchTimeoutRef = React.useRef<number | undefined>(undefined);
  const hoveredFeatureId = isUsingLocalHoverOverride
    ? localHoveredWarningId
    : (hoveredLinkedFeatureIds[0] ?? hoveredLocalWarningIds[0]);

  const handleHoverWarning = React.useCallback(
    (warningId?: string): void => {
      setLocalHoveredWarningId(warningId);
      setIsUsingLocalHoverOverride(true);

      if (hoverDispatchTimeoutRef.current) {
        window.clearTimeout(hoverDispatchTimeoutRef.current);
      }

      hoverDispatchTimeoutRef.current = window.setTimeout(() => {
        if (!ownedFeatureLayerIds.length && hasLinkedTimelineTarget) {
          dispatch(
            warningsFiltersActions.setHoveredWarningIdsForPanel({
              panelId,
              warningIds: warningId ? [warningId] : [],
            }),
          );
        }
        ownedFeatureLayerIds.forEach((layerId) => {
          dispatch(
            layerActions.setHoveredFeatures({
              layerId,
              hoveredFeatureIds: warningId ? [warningId] : [],
            }),
          );
        });
        setIsUsingLocalHoverOverride(false);
      }, WARNING_HOVER_DISPATCH_DELAY);
    },
    [dispatch, hasLinkedTimelineTarget, ownedFeatureLayerIds, panelId],
  );

  React.useEffect(() => {
    return (): void => {
      if (hoverDispatchTimeoutRef.current) {
        window.clearTimeout(hoverDispatchTimeoutRef.current);
      }

      const ownedLayerIds = genericSelectors.selectFeatureSyncLayerIdsForPanel(
        store.getState(),
        panelId,
      );
      const currentLinkedTargetIds = genericSelectors.getLinkedTargetIds(
        store.getState(),
        panelId,
      );
      const currentWorkspaceViewsById =
        syncGroupsSelector.getWorkspaceViewsById(store.getState()) as Record<
          string,
          { componentType?: string }
        >;
      const hasCurrentLinkedTimelineTarget = currentLinkedTargetIds.some(
        (targetId) =>
          currentWorkspaceViewsById[targetId]?.componentType ===
          'WarningsTimeline',
      );

      if (!ownedLayerIds.length && hasCurrentLinkedTimelineTarget) {
        dispatch(
          warningsFiltersActions.setHoveredWarningIdsForPanel({
            panelId,
            warningIds: [],
          }),
        );
      }

      ownedLayerIds.forEach((layerId) => {
        dispatch(
          layerActions.setHoveredFeatures({
            layerId,
            hoveredFeatureIds: [],
          }),
        );
      });
    };
  }, [dispatch, panelId, store]);

  return {
    handleHoverWarning,
    hoveredFeatureId,
  };
};
